import { TFunction } from 'i18next';

import { MultiColorDisplayOption } from './colorOption';
import { ShapeOption } from './shapeOptions';

export const SkinToneModelOptions = ['type1', 'type2', 'type3', 'type4', 'type5', 'type6'] as const;
export type SkinToneModel = (typeof SkinToneModelOptions)[number];
export interface SkinToneOption extends MultiColorDisplayOption {
  id: SkinToneModel;
}

export const skinToneTitle = (id: SkinToneModel, t: TFunction) => {
  switch (id) {
    case 'type1':
      return t('LOC_COLOR_LIGHT', { ns: 'model' });
    case 'type2':
      return t('LOC_COLOR_FAIR', { ns: 'model' });
    case 'type3':
      return t('LOC_COLOR_MEDIUM', { ns: 'model' });
    case 'type4':
      return t('LOC_COLOR_OLIVE', { ns: 'model' });
    case 'type5':
      return t('LOC_COLOR_DARK', { ns: 'model' });
    case 'type6':
      return t('LOC_COLOR_DEEP', { ns: 'model' });
  }
  throw new Error(`Unhandled option ${id}`);
};

const skinToneHexColorMapping: { [x in SkinToneModel]: string[] } = {
  type1: ['#F9EDE3', '#F6E7DA'],
  type2: ['#F8EADE', '#E7C2AA'],
  type3: ['#EDCEB9', '#DCAB82'],
  type4: ['#B08B57', '#977664'],
  type5: ['#C79067', '#57331C'],
  type6: ['#97725A', '#0A0A0A'],
};

export const buildSkinToneOptions = (t: TFunction): SkinToneOption[] => {
  return SkinToneModelOptions.map((id) => {
    const option: SkinToneOption = {
      id,
      title: skinToneTitle(id, t),
      hexColors: skinToneHexColorMapping[id],
    };
    return option;
  });
};

export const SkinTypeModelOptions = ['normal', 'dry', 'combination', 'oily', 'sensitive'] as const;
export type SkinTypeModel = (typeof SkinTypeModelOptions)[number];

export interface SkinTypeOption {
  id: SkinTypeModel;
  image: string;
}

export const SkinTypeOptions: SkinTypeOption[] = [
  { id: 'normal', image: 'skin-type-normal' },
  { id: 'dry', image: 'skin-type-dry' },
  { id: 'combination', image: 'skin-type-combination' },
  { id: 'oily', image: 'skin-type-oily' },
];

export const skinTypeTitle = (id: SkinTypeModel, t: TFunction) => {
  switch (id) {
    case 'normal':
      return t('LOC_SKIN_TYPE_NORMAL', { ns: 'model' });
    case 'dry':
      return t('LOC_SKIN_TYPE_DRY', { ns: 'model' });
    case 'combination':
      return t('LOC_SKIN_TYPE_COMBINATION', { ns: 'model' });
    case 'oily':
      return t('LOC_SKIN_TYPE_OILY', { ns: 'model' });
    case 'sensitive':
      return t('LOC_SKIN_TYPE_SENSITIVE', { ns: 'model' });
  }
  throw new Error(`Unhandled option ${id}`);
};

export const SkinUndertoneModelOptions = ['cool', 'neutral', 'warm'] as const;
export type SkinUndertoneModel = (typeof SkinUndertoneModelOptions)[number];

export function skinUndertoneTitle(skinUndertone: SkinUndertoneModel, t: TFunction): string {
  switch (skinUndertone) {
    case 'cool':
      return t('LOC_SKIN_UNDERTONE_COOL', { ns: 'model' });
    case 'neutral':
      return t('LOC_SKIN_UNDERTONE_NEUTRAL', { ns: 'model' });
    case 'warm':
      return t('LOC_SKIN_UNDERTONE_WARM', { ns: 'model' });
  }
  throw new Error(`Invalid tummy shape option ${skinUndertone}`);
}

export function skinUndertoneHelp(skinUndertone: SkinUndertoneModel, t: TFunction): string {
  switch (skinUndertone) {
    case 'cool':
      return t('LOC_SKIN_UNDERTONE_HELP_COOL', { ns: 'model' });
    case 'neutral':
      return t('LOC_SKIN_UNDERTONE_HELP_NEUTRAL', { ns: 'model' });
    case 'warm':
      return t('LOC_SKIN_UNDERTONE_HELP_WARM', { ns: 'model' });
  }
  throw new Error(`Invalid tummy shape option ${skinUndertone}`);
}

export const buildSkinUndertoneOptions = (t: TFunction): ShapeOption[] => {
  return SkinUndertoneModelOptions.map((undertone, index) => {
    const shapeOption: ShapeOption = {
      shape: index,
      name: skinUndertoneTitle(undertone, t),
      image: imagePathForUndertoneOption(undertone),
    };
    return shapeOption;
  });
};

function imagePathForUndertoneOption(undertone: SkinUndertoneModel): string {
  switch (undertone) {
    case 'cool':
      return 'skin-undertone-cool';
    case 'neutral':
      return 'skin-undertone-neutral';
    case 'warm':
      return 'skin-undertone-warm';
  }
  throw new Error(`Invalid hip shape option ${undertone}`);
}

export const SkinSensitivityModelOptions = ['none', 'little', 'very'] as const;
export type SkinSensitivityModel = (typeof SkinSensitivityModelOptions)[number];

export const SkinSensitivityOptions = SkinSensitivityModelOptions;

export const skinSensitivityTitle = (id: SkinSensitivityModel, t: TFunction) => {
  switch (id) {
    case 'none':
      return t('LOC_SKIN_SENSITIVITY_NONE', { ns: 'model' });
    case 'little':
      return t('LOC_SKIN_SENSITIVITY_MILD', { ns: 'model' });
    case 'very':
      return t('LOC_SKIN_SENSITIVITY_VERY', { ns: 'model' });
  }
  throw new Error(`Unhandled option ${id}`);
};
